import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { FixedSizeList as List } from 'react-window';
import LoadingIndicator from '@Common/LoadingIndicator/LoadingIndicator';
import Notification from '@Common/Notification';
import * as types from '@Constants/actionTypes';
import './BulkPushdownDrawer.scss';

type CheckedTask = {
    id: number;
};

type LocationItem = {
    id: number;
    name: string;
};

type BulkPushdownDrawerProps = {
    baselineId: string;
    projectId: number;
    checkedTasks: CheckedTask[];
    onComplete: () => void;
    onCancel: () => void;
    baselineTaskActions: {
        bulkPushdown: (request: { baselineTaskIds: number[]; locationIds: number[]; projectId: number }) => any;
        bulkLocationPushdownSearch: (params: { term: string; isNameSearch: boolean; baselineId: string; projectId: string }) => any;
    };
};

const BulkPushdownDrawer = ({ baselineId, projectId, checkedTasks, onComplete, onCancel, baselineTaskActions }: BulkPushdownDrawerProps) => {
    const [availableLocations, setAvailableLocations] = useState<LocationItem[]>([]);
    const [selectedLocations, setSelectedLocations] = useState<LocationItem[]>([]);
    const [availableFilter, setAvailableFilter] = useState('');
    const [selectedFilter, setSelectedFilter] = useState('');
    const [isLocationsLoading, setIsLocationsLoading] = useState(false);
    const [isPushdownLoading, setIsPushdownLoading] = useState(false);

    const baselineTaskIds = useMemo(() => checkedTasks.map((x) => x.id), [checkedTasks]);
    const projectIdString = projectId.toString();

    useEffect(() => {
        loadLocations();
    }, []);

    const loadLocations = async () => {
        try {
            setIsLocationsLoading(true);
            const result = await baselineTaskActions.bulkLocationPushdownSearch({
                term: '',
                isNameSearch: true,
                baselineId,
                projectId: projectIdString,
            });
            if (result.type === types.SEARCH_BULK_LOCATION_PUSHDOWN_SUCCESS) {
                setAvailableLocations(result.payload.locations);
            } else if (result.type === types.SEARCH_BULK_LOCATION_PUSHDOWN_FAILURE) {
                Notification('error', 'An Error Occurred', result.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            setIsLocationsLoading(false);
        }
    };

    const selectLocation = (locId: number) => {
        const location = availableLocations.find((x) => x.id === locId);
        if (!location) return;
        setAvailableLocations((prev) => prev.filter((x) => x.id !== locId));
        setSelectedLocations((prev) => [...prev, location]);
    };

    const deselectLocation = (locId: number) => {
        const location = selectedLocations.find((x) => x.id === locId);
        if (!location) return;
        setSelectedLocations((prev) => prev.filter((x) => x.id !== locId));
        setAvailableLocations((prev) => [...prev, location]);
    };

    const selectAll = () => {
        setSelectedLocations((prev) => [...prev, ...availableLocations]);
        setAvailableLocations([]);
    };

    const deselectAll = () => {
        setAvailableLocations((prev) => [...prev, ...selectedLocations]);
        setSelectedLocations([]);
    };

    const onPushdown = async () => {
        try {
            setIsPushdownLoading(true);
            const request = {
                baselineTaskIds,
                locationIds: selectedLocations.map((x) => x.id),
                projectId,
            };
            const response = await baselineTaskActions.bulkPushdown(request);
            if (response.type === types.PERFORM_BULK_PUSHDOWN_SUCCESS) {
                Notification('success', 'Success', 'Bulk Pushdown completed successfully');
                onComplete();
            } else if (response.type === types.PERFORM_BULK_PUSHDOWN_FAILURE) {
                Notification('error', 'An Error Occurred', response.payload.message);
                setIsPushdownLoading(false);
            } else {
                Notification('error', 'An Error Occurred', 'Unexpected response from bulk pushdown.');
                setIsPushdownLoading(false);
            }
        } catch (error) {
            Notification('error', 'Error', error);
            setIsPushdownLoading(false);
        }
    };

    const filteredAvailable = availableLocations
        .filter((x) => x.name.toLocaleLowerCase().includes(availableFilter.toLocaleLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name));

    const filteredSelected = selectedLocations
        .filter((x) => x.name.toLocaleLowerCase().includes(selectedFilter.toLocaleLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name));

    return (
        <Modal show={true} onHide={onCancel} size='xl' dialogClassName='bulk-pushdown-modal'>
            <LoadingIndicator isLoading={isPushdownLoading}>
                <Modal.Header closeButton>
                    <Modal.Title>Bulk Pushdown</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <LoadingIndicator isLoading={isLocationsLoading} hideWhileLoading={true}>
                        <div className='bulk-pushdown-locations'>
                            <div className='container'>
                                <div className='row'>
                                    <div className='col-md-6'>
                                        <div className='row locations-section-top'>
                                            <div className='col my-3'>
                                                <span className='locations-section-title'>Available</span>
                                            </div>
                                        </div>
                                        <div className='row locations-section'>
                                            <div className='col-12 py-2'>
                                                <div className='input-group'>
                                                    <input
                                                        type='text'
                                                        className='form-control'
                                                        placeholder='Search by name'
                                                        value={availableFilter}
                                                        onChange={(e) => setAvailableFilter(e.target.value)}
                                                    />
                                                    <span className='input-group-text bg-transparent'>
                                                        <i className='far fa-magnifying-glass'></i>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className='row locations-section'>
                                            <div className='col-12 list-container'>
                                                <List
                                                    className='list-group'
                                                    height={350}
                                                    itemCount={filteredAvailable.length}
                                                    itemSize={50}
                                                    width={'100%'}
                                                    itemData={filteredAvailable}
                                                    itemKey={(index, data) => data[index].id}>
                                                    {({ data, index, style }) => {
                                                        const item = data[index];
                                                        return (
<button type='button' style={style} className='list-group-item' onClick={() => selectLocation(item.id)}>
    <div className='d-flex w-100 justify-content-between'>
        <span className='list-item-text align-middle'>{item.name}</span>
        <div className='list-item-icon'>
            <i className='fa-regular fa-plus'></i>
        </div>
    </div>
</button>
                                                        );
                                                    }}
                                                </List>
                                            </div>
                                        </div>
                                        <div className='row locations-section-bottom'>
                                            <div className='col-12'>
                                                <button type='button' className='btn pacer61-purple-btn' onClick={selectAll}>
                                                    Select All
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className='col-md-6'>
                                        <div className='row locations-section-top'>
                                            <div className='col my-3'>
                                                <span className='locations-section-title'>Selected</span>
                                            </div>
                                        </div>
                                        <div className='row locations-section'>
                                            <div className='col-12 py-2'>
                                                <div className='input-group'>
                                                    <input
                                                        type='text'
                                                        className='form-control'
                                                        placeholder='Search by name'
                                                        value={selectedFilter}
                                                        onChange={(e) => setSelectedFilter(e.target.value)}
                                                    />
                                                    <span className='input-group-text bg-transparent'>
                                                        <i className='far fa-magnifying-glass'></i>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className='row locations-section'>
                                            <div className='col-12 list-container'>
                                                <List
                                                    className='list-group'
                                                    height={350}
                                                    itemCount={filteredSelected.length}
                                                    itemSize={50}
                                                    width={'100%'}
                                                    itemData={filteredSelected}
                                                    itemKey={(index, data) => data[index].id}>
                                                    {({ data, index, style }) => {
                                                        const item = data[index];
                                                        return (
<button type='button' style={style} className='list-group-item' onClick={() => deselectLocation(item.id)}>
    <div className='d-flex w-100 justify-content-between'>
        <span className='list-item-text align-middle'>{item.name}</span>
        <div className='list-item-icon'>
            <i className='fa-regular fa-minus'></i>
        </div>
    </div>
</button>
                                                        );
                                                    }}
                                                </List>
                                            </div>
                                        </div>
                                        <div className='row locations-section-bottom'>
                                            <div className='col-12'>
                                                <button type='button' className='btn pacer61-purple-btn' onClick={deselectAll}>
                                                    Deselect All
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </LoadingIndicator>
                </Modal.Body>
                <Modal.Footer>
                    <span className='me-auto'>Push task(s) to {selectedLocations.length} locations</span>
                    <Button variant='secondary' onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button variant='primary' onClick={onPushdown}>
                        Pushdown
                    </Button>
                </Modal.Footer>
            </LoadingIndicator>
        </Modal>
    );
};

export default BulkPushdownDrawer;
